import React, { useCallback, useEffect, useState } from 'react'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileEmptyState,
  MobileStatStrip,
  mobile,
} from '../components/mobile'
import {
  PlusIcon,
  CalendarIcon,
  ClockIcon,
  AlertCircleIcon,
  Loader2Icon,
  ZapIcon,
  RotateCwIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { fetchQcRotationQueue, requestQcRush, clusterQcDayBatch, type QcRotationContract } from '../lib/qcRotationQueue'
import { fetchQcInspectionsList } from '../lib/qcInspectionsQuery'
import { ColumnSelector } from '../components/ColumnSelector'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { useDivision } from '../contexts/DivisionContext'
import { formatEmployeeLabel } from '../lib/formatEmployeeLabel'

interface InspectorOption {
  id: string
  first_name: string
  last_name: string
  employee_number?: string | null
}

interface UpcomingInspection {
  id: string
  scheduled_date?: string | null
  status: string
  priority?: string | null
  contract_id?: string | null
  job_id?: string | null
  inspector_id?: string | null
  notes?: string | null
  contract?: { contract_number?: string | null; title?: string | null } | null
  job?: { title?: string | null; job_number?: string | null } | null
  inspector?: { first_name?: string; last_name?: string } | null
}

export function QCSchedule() {
  const { currentDivision } = useDivision()
  const [queue, setQueue] = useState<QcRotationContract[]>([])
  const [upcoming, setUpcoming] = useState<UpcomingInspection[]>([])
  const [inspectors, setInspectors] = useState<InspectorOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ contractId: '', inspectorId: '', scheduledDate: '', priority: 'medium', notes: '' })
  const [submitting, setSubmitting] = useState(false)
  const [buildingBatch, setBuildingBatch] = useState(false)
  const [dayBatch, setDayBatch] = useState<QcRotationContract[]>([])
  const [batchCapacity, setBatchCapacity] = useState(8)

  const loadQueue = useCallback(async () => {
    try {
      setError(null)
      const rows = await fetchQcRotationQueue(100, currentDivision)
      setQueue(rows)
    } catch (err: any) {
      console.error('Error fetching QC rotation queue:', err)
      setError(err.message || 'Failed to load QC rotation queue')
    }
  }, [currentDivision])

  const loadUpcoming = useCallback(async () => {
    const { data, error: fetchError } = await fetchQcInspectionsList({
      statusEq: 'scheduled',
      orderBy: 'scheduled_date',
      ascending: true,
      limit: 50,
      division: currentDivision,
    })

    if (fetchError) {
      console.error('Error fetching upcoming QC visits:', fetchError)
      return
    }
    setUpcoming((data || []) as UpcomingInspection[])
  }, [currentDivision])

  const loadInspectors = useCallback(async () => {
    const { data } = await supabase
      .from('employees')
      .select('id, first_name, last_name, employee_number')
      .eq('is_active', true)
      .order('first_name')
    setInspectors((data || []) as InspectorOption[])
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    await Promise.all([loadQueue(), loadUpcoming(), loadInspectors()])
    setLoading(false)
  }, [loadQueue, loadUpcoming, loadInspectors])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const handleRequestRush = async (contractId: string) => {
    try {
      await requestQcRush(contractId)
      await loadQueue()
      openScheduleForm(contractId)
    } catch (err: any) {
      alert('Failed to push QC to front: ' + (err.message || 'Unknown error'))
    }
  }

  const handleBuildTodayBatch = () => {
    setBuildingBatch(true)
    try {
      const batch = clusterQcDayBatch(queue, batchCapacity, 20)
      setDayBatch(batch)
    } finally {
      setBuildingBatch(false)
    }
  }

  const scheduleBatchForToday = async () => {
    if (dayBatch.length === 0) return
    if (!inspectors[0]) {
      alert('Add at least one active employee before scheduling a QC batch.')
      return
    }
    try {
      setSubmitting(true)
      const today = new Date().toISOString().split('T')[0]
      const rows = dayBatch.map((c, i) => ({
        contract_id: c.id,
        inspector_id: inspectors[i % inspectors.length].id,
        scheduled_date: today,
        status: 'scheduled',
        priority: c.qc_rush_requested_at ? 'high' : 'medium',
        notes: `Rotation batch ${i + 1}/${dayBatch.length}`,
      }))
      const { error } = await supabase.from('qc_inspections').insert(rows)
      if (error) throw error
      setDayBatch([])
      await loadUpcoming()
    } catch (err: any) {
      alert('Failed to schedule batch: ' + (err.message || 'Unknown error'))
    } finally {
      setSubmitting(false)
    }
  }

  const openScheduleForm = (contractId?: string) => {
    setForm({ contractId: contractId || '', inspectorId: '', scheduledDate: '', priority: 'medium', notes: '' })
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.contractId || !form.scheduledDate) return
    try {
      setSubmitting(true)
      const { error: insertError } = await supabase.from('qc_inspections').insert({
        contract_id: form.contractId,
        inspector_id: form.inspectorId || null,
        scheduled_date: form.scheduledDate,
        priority: form.priority,
        notes: form.notes || null,
        status: 'scheduled',
      })
      if (insertError) throw insertError

      setShowForm(false)
      await Promise.all([loadQueue(), loadUpcoming()])
    } catch (err: any) {
      console.error('Error scheduling QC visit:', err)
      alert('Failed to schedule QC visit: ' + (err.message || 'Unknown error'))
    } finally {
      setSubmitting(false)
    }
  }

  const getPriorityColor = (priority?: string | null) => {
    switch (priority) {
      case 'high':
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'low':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-yellow-100 text-yellow-800'
    }
  }

  const rushCount = queue.filter((c) => !!c.qc_rush_requested_at).length
  const neverInspectedCount = queue.filter((c) => !c.last_qc_at).length

  const QC_QUEUE_COLUMNS = [
    { id: 'contract', label: 'Contract' },
    { id: 'customer', label: 'Customer' },
    { id: 'lastQc', label: 'Last QC' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(QC_QUEUE_COLUMNS, { storageKey: 'qc_schedule_queue' })

  if (loading) {
    return (
      <div className="space-y-6 px-4 flex items-center justify-center min-h-[400px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <MobilePage>
      <div>
        <MobilePageHeader title="QC Schedule" subtitle={<span className="hidden sm:inline">Upcoming inspections and rotation queue</span>} />
        <p className="mt-1 text-gray-600">Rotate quality control inspections across active contracts</p>
      </div>

      {error && (
        <div className="mb-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Stats Cards */}
      <div className="hidden md:grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Contracts in Rotation</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{queue.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              <RotateCwIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Rush Requested</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{rushCount}</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 text-red-600">
              <ZapIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Never Inspected</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{neverInspectedCount}</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
              <AlertCircleIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Upcoming Visits</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{upcoming.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <CalendarIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Form */}
      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Schedule QC Visit</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contract</label>
              <select
                required
                value={form.contractId}
                onChange={(e) => setForm({ ...form, contractId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a contract...</option>
                {queue.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.contract_number ? `#${c.contract_number} - ` : ''}
                    {c.title || 'Contract'} ({c.customer?.company_name || 'No customer'})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Inspector</label>
              <select
                value={form.inspectorId}
                onChange={(e) => setForm({ ...form, inspectorId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Unassigned</option>
                {inspectors.map((i) => (
                  <option key={i.id} value={i.id}>
                    {formatEmployeeLabel(i)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
              <input
                type="date"
                required
                value={form.scheduledDate}
                onChange={(e) => setForm({ ...form, scheduledDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Scheduling…' : 'Schedule QC Visit'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {dayBatch.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-emerald-900">Today&apos;s QC batch ({dayBatch.length})</h3>
              <p className="text-xs text-emerald-800 mt-0.5">
                Seeded from the front of the queue, then nearby addresses (same zip / close coords) within the next ~20 in line.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDayBatch([])}
                className="px-3 py-1.5 text-sm border border-emerald-300 rounded-lg text-emerald-800 hover:bg-emerald-100"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={scheduleBatchForToday}
                disabled={submitting}
                className="px-3 py-1.5 text-sm bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-50"
              >
                {submitting ? 'Scheduling…' : 'Schedule all for today'}
              </button>
            </div>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {dayBatch.map((c, i) => (
              <li key={c.id} className="bg-white border border-emerald-100 rounded px-3 py-2 flex gap-2">
                <span className="font-semibold text-emerald-700 w-6">{i + 1}.</span>
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate">{c.title || c.contract_number}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {c.property?.address || c.customer?.company_name || '—'}
                    {c.property?.zip_code ? ` · ${c.property.zip_code}` : ''}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Rotation Queue */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Rotation Queue</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-gray-600 flex items-center gap-1">
              Day size
              <input
                type="number"
                min={1}
                max={30}
                value={batchCapacity}
                onChange={(e) => setBatchCapacity(Number(e.target.value) || 8)}
                className="w-14 px-1.5 py-1 border border-gray-300 rounded text-sm"
              />
            </label>
            <button
              type="button"
              onClick={handleBuildTodayBatch}
              disabled={buildingBatch || queue.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm disabled:opacity-50"
            >
              <RotateCwIcon className="w-4 h-4" />
              {buildingBatch ? 'Building…' : "Build today's list"}
            </button>
            <ColumnSelector columns={QC_QUEUE_COLUMNS} visibleColumns={visibleColumns} onToggle={toggleColumn} />
            <button
              onClick={() => openScheduleForm()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <PlusIcon className="w-4 h-4" />
              Schedule QC Visit
            </button>
          </div>
        </div>
        {queue.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No active contracts found.</div>
        ) : (
          <>
      <p className={mobile.sectionLabel}>Rotation queue</p>
      <div className="native-mobile-list space-y-2">
        {queue.length === 0 ? (
          <MobileEmptyState title="Queue empty" description="No contracts in the rotation queue." />
        ) : (
          queue.slice(0, 40).map((c) => (
            <MobileListRow
              key={c.id}
              title={c.title || "Contract"}
              subtitle={c.contract_number ? `#${c.contract_number}` : c.customer?.company_name}
              badges={
                <>
                  {c.qc_rush_requested_at ? (
                    <span className={`${mobile.badge} bg-red-50 text-red-700`}>Rush</span>
                  ) : null}
                  {!c.last_qc_at ? (
                    <span className={`${mobile.badge} bg-amber-50 text-amber-700`}>Never</span>
                  ) : null}
                </>
              }
              meta={<div>Last QC: {c.last_qc_at || "—"}</div>}
              chevron={false}
            />
          ))
        )}
      </div>

      <div className="native-desktop-table overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {visibleColumns.has('contract') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contract</th>}
                  {visibleColumns.has('customer') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>}
                  {visibleColumns.has('lastQc') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last QC</th>}
                  {visibleColumns.has('actions') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {queue.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    {visibleColumns.has('contract') && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{c.title || 'Contract'}</div>
                        {c.contract_number && <div className="text-xs text-gray-500">#{c.contract_number}</div>}
                      </td>
                    )}
                    {visibleColumns.has('customer') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {c.customer?.company_name || '—'}
                      </td>
                    )}
                    {visibleColumns.has('lastQc') && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {c.qc_rush_requested_at && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 mr-2">
                            <ZapIcon className="w-3 h-3" /> Rush
                          </span>
                        )}
                        <span className="text-sm text-gray-600">
                          {c.last_qc_at ? new Date(c.last_qc_at).toLocaleDateString() : 'Never'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.has('actions') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-3">
                          <button onClick={() => openScheduleForm(c.id)} className="text-blue-600 hover:text-blue-900 font-medium">
                            Schedule
                          </button>
                          {!c.qc_rush_requested_at && (
                            <button onClick={() => handleRequestRush(c.id)} className="text-red-600 hover:text-red-900 font-medium">
                              Inspect now
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Upcoming Visits */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming QC Visits</h2>
        </div>
        {upcoming.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No QC visits scheduled.</div>
        ) : (
          <>
      <p className={mobile.sectionLabel}>Upcoming visits</p>
      <div className="native-mobile-list space-y-2">
        {upcoming.length === 0 ? (
          <MobileEmptyState title="No upcoming visits" />
        ) : (
          upcoming.map((v) => (
            <MobileListRow
              key={v.id}
              title={v.contract?.title || v.job?.title || "Inspection"}
              subtitle={v.contract?.contract_number || v.job?.job_number || undefined}
              badges={<span className={`${mobile.badge} bg-blue-50 text-blue-700`}>{v.status}</span>}
              meta={
                <>
                  <div>{v.scheduled_date || "Unscheduled"}</div>
                  <div>
                    {v.inspector
                      ? `${v.inspector.first_name || ""} ${v.inspector.last_name || ""}`.trim()
                      : "Unassigned"}
                  </div>
                </>
              }
              chevron={false}
            />
          ))
        )}
      </div>

      <div className="native-desktop-table overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contract / Job</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inspector</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {upcoming.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {v.contract?.title || v.job?.title || 'QC Visit'}
                      {v.contract?.contract_number && <span className="text-xs text-gray-400 ml-1">#{v.contract.contract_number}</span>}
                      {v.job?.job_number && <span className="text-xs text-gray-400 ml-1">#{v.job.job_number}</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {v.inspector ? `${v.inspector.first_name || ''} ${v.inspector.last_name || ''}`.trim() : 'Unassigned'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex items-center gap-1.5">
                      <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                      {v.scheduled_date ? new Date(v.scheduled_date + 'T00:00:00').toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(v.priority)}`}>
                        {v.priority || 'medium'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </MobilePage>
  )
}